import React, {Component} from 'react';
import {toast} from 'react-toastify';
import {withTranslation} from 'react-i18next';
import {APIClient} from "../../../utils/APIClient";
import {URLQueryStringParser} from "../../../utils/URLQueryStringParser";
import {API_URL} from '../../../settings/local_settings';
import {Loading} from "../../Loading";
import {withRouterParams} from "../../../utils/withRouterParams";
import { GOOGLE_CHANNEL } from '../../channels/channels';
import {clearOauthReturn, destinationOf, errorDestinationOf, peekOauthReturn} from '../../onboarding/oauthReturn';
import {consumeReconnect, forgetReconnect} from '../connections/reconnectNote';
import {
    announceReconnectNotice, destinationAfterConnect, originOf, reconnectNotice, reconnectResultOf, recordReconnectResult,
} from '../connections/reconnectAccountText';

let oauthExchangeInFlight = false;

/**
 * El código del error, sólo cuando DICE algo.
 *
 * `ERROR` es el default de `ResultError` y el relleno del propio `APIClient`: significa exactamente
 * lo mismo que no mandar nada, así que viajaría hasta la URL para terminar cayendo en el mismo copy
 * genérico. Un parámetro que no cambia nada es ruido en una barra de direcciones que el usuario ve.
 */
function motivoDe(data) {
    const codigo = data && (data.error_code || data.status);
    return codigo && codigo !== 'ERROR' ? codigo : null;
}


class ConnectAdwordsAccount extends Component {
    constructor(props) {
        super(props);
        this.componentDidMount = this.componentDidMount.bind(this);
    }

    componentDidMount() {
        // La nota se lee UNA vez, acá, y el destino viaja con el resto del componente.
        //
        // Antes se leía al final, dentro del `.then()` del POST, y para entonces podía estar
        // borrada: las dos ramas de guarda de más abajo devuelven al usuario al túnel, montar el
        // túnel dispara la limpieza que borra la nota, y el POST —que sigue en vuelo— aterrizaba
        // después sin nota y mandaba al usuario a la pantalla clásica ENCIMA del recorrido al que
        // acababa de volver. Es lo que se vio el 08/09 conectando Google desde el paso 2: el
        // usuario terminaba en `/agency/conections?just_connected=GOOGLE`, con el modal viejo.
        const tunnelReturn = peekOauthReturn();
        const inTunnel = Boolean(tunnelReturn);

        /**
         * Navegar y CONSUMIR la nota, que es de un solo uso.
         *
         * Se consume acá y no al leerla porque el destino se decide al montar (ver arriba), pero
         * recién en estas ramas el recorrido termina de verdad. Dos cosas se rompían si sobrevivía:
         * el botón «atrás» volvía a esta pantalla sin `code` —lo limpia el `replaceState` de acá
         * abajo—, encontraba la nota viva y pintaba la tarjeta del canal como FALLIDA sobre una
         * conexión que acababa de funcionar; y, hasta quince minutos después, la próxima conexión
         * hecha desde la pantalla clásica aterrizaba en el túnel, sin los avisos de la pantalla en
         * la que el usuario estaba.
         *
         * Las dos guardas de doble montaje de más abajo NO la consumen a propósito: vuelven a pasar
         * por acá y todavía no terminó nada.
         */
        const irA = destino => {
            this.props.history.push(destino);
            clearOauthReturn();
        };
        let code = new URLQueryStringParser().getUrlParams('code');
        if (!code) {
            // Volvió sin autorizar: la reconexión que estaba anotada terminó acá (contrato §2.1).
            forgetReconnect();
            // `access_denied` es que la persona canceló en la pantalla de Google: no es una falla, y el
            // túnel lo dice como Meta («quedó sin completar»), no como «suele ser un permiso que
            // falta». Además separa los cancelados de los errores en la medición (UD-23).
            const cancelo = new URLQueryStringParser().getUrlParams('error') === 'access_denied';
            irA(errorDestinationOf(tunnelReturn, '/agency/conections', GOOGLE_CHANNEL, cancelo ? 'CANCELLED' : null));
            return;
        }
        try {
            const url = new URL(window.location.href);
            ['code', 'scope', 'authuser', 'prompt', 'state', 'hd'].forEach((p) => url.searchParams.delete(p));
            window.history.replaceState({}, '', url.pathname + (url.search || ''));
        } catch (e) { /* noop */ }
        if (oauthExchangeInFlight) {
            // Sin consumir la nota: esta rama existe justamente porque el componente se monta dos
            // veces, y gastarla acá dejaría al segundo pasaje sin destino al que volver.
            this.props.history.push(tunnelReturn || '/agency/conections?just_connected=' + GOOGLE_CHANNEL);
            return;
        }
        const guardKey = 'connecting_google_oauth_code';
        if (sessionStorage.getItem(guardKey) === code) {
            // Sin consumir la nota: esta rama existe justamente porque el componente se monta dos
            // veces, y gastarla acá dejaría al segundo pasaje sin destino al que volver.
            this.props.history.push(tunnelReturn || '/agency/conections?just_connected=' + GOOGLE_CHANNEL);
            return;
        }
        oauthExchangeInFlight = true;
        sessionStorage.setItem(guardKey, code);
        let credential_label = localStorage.getItem('pending_credential_label');
        localStorage.removeItem('pending_credential_label');
        // Qué se estaba reconectando (la cuenta de un «Reconectar», o la conexión de Plataformas): se
        // consume acá, salga bien o mal, y sólo si es de Google (`reconnectNote`).
        const reconnect = consumeReconnect(GOOGLE_CHANNEL);
        const origin = originOf(reconnect);
        const postData = {'code': code, 'credential_label': credential_label};
        if (reconnect && reconnect.adAccountId) postData.reconnect_ad_account_id = reconnect.adAccountId;
        if (reconnect && reconnect.credentialId) postData.reconnect_credential_id = reconnect.credentialId;
        /**
         * El fallo, con su MOTIVO cuando el backend lo dice.
         *
         * A pantalla sigue sin salir el mensaje del backend —puede nombrar a otra agencia («esa
         * cuenta ya está conectada a…»)— pero el CÓDIGO sí viaja: es un enum, no lleva información
         * de nadie, y es lo que le permite al túnel decir «esa cuenta de Google no tiene ninguna
         * cuenta de Google Ads» en vez de «suele ser un permiso que falta».
         */
        const fallo = (data) => {
            oauthExchangeInFlight = false;
            sessionStorage.removeItem(guardKey);
            if (!inTunnel) toast.error(this.props.t('google_link.error.generic'));
            irA(errorDestinationOf(tunnelReturn, '/agency/conections', GOOGLE_CHANNEL, motivoDe(data)));
        };
        new APIClient(API_URL + 'get_access_tokens/google/').call('POST', postData).then((data) => {
            if (data && data.status === 'OK') {
                // Con el backend nuevo la cuenta ya quedó en su conexión y el destino abre ESE acceso;
                // con uno viejo, Conexiones la reasigna (`reconnect_ad_account_id`), como antes.
                const result = reconnectResultOf(data);
                recordReconnectResult(GOOGLE_CHANNEL, result, origin);
                irA(destinationOf(tunnelReturn, destinationAfterConnect(GOOGLE_CHANNEL, data, reconnect)));
                if (!inTunnel) {
                    const notice = reconnectNotice(this.props.t, GOOGLE_CHANNEL, result, origin);
                    if (notice) announceReconnectNotice(notice);
                    else toast.success(this.props.t('google_link.connected'));
                }
            } else {
                // Todo lo que no es OK es el mismo camino: antes había dos ramas idénticas —una para
                // `status === 'ERROR'` y otra para "cualquier otra cosa"— y eso se volvió un defecto
                // el día que el backend empezó a contestar con códigos TIPADOS: un
                // `status: 'GOOGLE_NO_ADS_ACCESS'` ya no entraba por la rama del error.
                fallo(data);
            }
        }).catch(() => fallo(null))
    }

    render() {
            return <Loading message={this.props.t('connections.oauth.connecting')}/>
    }
}

export default withTranslation()(withRouterParams(ConnectAdwordsAccount));
